import React, { useState, useEffect } from 'react';
import { Button, Alert } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { Task } from '../../../types/Task';

export type ConvertDirection = 'toQuantified' | 'toCustom';

interface ConvertTaskTypeModalProps {
  visible: boolean;
  task: Task | null;
  direction: ConvertDirection | null;
  onClose: () => void;
  onConfirm: (payload: { targetType: 'Quantified' | 'Custom'; unitOfMeasure?: string; identifiedQuantity?: number }) => Promise<void>;
}

const ConvertTaskTypeModal: React.FC<ConvertTaskTypeModalProps> = ({
  visible,
  task,
  direction,
  onClose,
  onConfirm
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && task) {
      setError(null);
    }
  }, [visible, task]);

  const handleConfirm = async () => {
    if (!task || !direction) return;
    setError(null);
    setSubmitting(true);
    try {
      if (direction === 'toCustom') {
        await onConfirm({ targetType: 'Custom' });
      } else {
        await onConfirm({ targetType: 'Quantified' });
      }
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to convert task.');
    } finally {
      setSubmitting(false);
    }
  };

  const isToCustom = direction === 'toCustom';
  const title = isToCustom ? 'Convert to Custom Task' : 'Convert to Quantified Task';

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Converting…' : 'Confirm'}
          </Button>
        </>
      }
    >
      {task && (
        <>
          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle tw-mr-2"></i>
              {error}
            </Alert>
          )}
          {isToCustom ? (
            <Alert variant="info">
              <p className="tw-mb-2">
                This task will become a <strong>Custom</strong> task. Related data (quantified details, GPS, costs) is
                kept so it can be reported in D2/D3 with <strong>0 accomplishment</strong>. If it was previously
                finalized, it will be closed and reported as 0.
              </p>
              <p className="tw-mb-0">
                Costs and task history are preserved. This action will be recorded in task history.
              </p>
            </Alert>
          ) : (
            <Alert variant="info">
              <p className="tw-mb-0">
                This task will become a <strong>Quantified</strong> task. After you confirm, the quantified task form
                will open so you can enter activity, unit of measure, planned accomplishment, and any other details.
                Costs and task history are preserved.
              </p>
            </Alert>
          )}
          <p className="tw-mt-3 tw-mb-0 tw-text-content-muted tw-text-sm">
            Task: {task.taskNumber || task.id} {task.description ? `— ${task.description}` : ''}
          </p>
        </>
      )}
    </ResponsiveModal>
  );
};

export default ConvertTaskTypeModal;
